import { ofetch, type $Fetch, type ResolvedFetchOptions } from 'ofetch'

import { clearSessionCookies } from './authMiddleware'
import { classifyErrorToast } from './errorToastRules'
import { getGetCurrentSessionQueryKey } from './orval/apiHooks'
import { formatErrorMessage } from './orvalHandleErrors'
import { qc } from '@app/_TSQueryProvider'
import { emitError } from '@features/observability/lib/errorBus'
import { backendUrl } from '@lib/constants'
import { getCsrfToken, setCsrfToken } from '@lib/csrfStore'
import { emitToast } from '@lib/emitToast'
import { buildMockParams, getMockConfig, isMockModeActive } from '@lib/mockMode'
import { normalizeUrlPath } from '@lib/normalizeRoute'
import { getPrimeMode, PRIME_REQUEST_HEADER } from '@lib/primeMode'

export type RawData = { _data: { error: Any; [key: string]: Any } }
export type ResponseWithData = Response & RawData
export type OptionsOrvalFetcher = ResolvedFetchOptions<Any, Any>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Any = any

const BASE_URL = backendUrl
export const IGNORE_VALIDATION_PATHS = ['/login']
const CHECK_SESSION_URL = `${BASE_URL}/admin/api/session`
export const CLEAR_COOKIE_STATUSES = [204, 401]

export const shouldIgnorePath = () =>
  typeof window !== 'undefined' && IGNORE_VALIDATION_PATHS.includes(window.location.pathname)

export async function checkSession() {
  const { status } = await fetch(CHECK_SESSION_URL, {
    method: 'GET',
    credentials: 'include',
  })

  if (CLEAR_COOKIE_STATUSES.includes(status)) {
    qc.invalidateQueries({ queryKey: getGetCurrentSessionQueryKey() })
    clearSessionCookies()
  }
}

export const onRequest = (options: ResolvedFetchOptions<Any, Any>, url?: string) => {
  if (!(options.headers instanceof Headers)) {
    options.headers = new Headers(options.headers || {})
  }

  const isFormData = options.body instanceof FormData
  if (!isFormData && !options.headers.has('Content-Type')) {
    options.headers.set('Content-Type', 'application/json')
  }

  const csrfToken = getCsrfToken()
  if (csrfToken) {
    options.headers.set('X-CSRF-Token', csrfToken)
  }

  if (getPrimeMode()) {
    options.headers.set(PRIME_REQUEST_HEADER, 'true')
  }

  // Mock mode (dev/QA only): mock every route via Prism, except routes that must NOT be mocked.
  const MOCK_EXCLUDED_ROUTES = ['api/session', 'filter_metadata', 'search_filters', 'widgets']
  if (isMockModeActive() && url && !MOCK_EXCLUDED_ROUTES.some((route) => url.includes(route))) {
    options.query = { ...options.query, ...buildMockParams(getMockConfig()) }
  }
}

export const onResponse = (response: Response) => {
  const t = response.headers.get('Set-Csrf-Token') || response.headers.get('set-csrf-token')

  if (t) {
    setCsrfToken(t)
  }
}

export const onResponseError = async (response: ResponseWithData) => {
  const errorData = response._data
  const decision = classifyErrorToast({ url: response.url, status: response.status, errorData })

  // Report server errors to Datadog via the bus. Skip 4xx — they are expected
  // validation/auth failures and would only burn budget. Fingerprint by
  // status + normalized path so one bad endpoint collapses to a single entry.
  if (response.status >= 500) {
    const path = normalizeUrlPath(response.url)
    emitError({
      message: `API ${response.status} ${path}`,
      source: 'api',
      fingerprint: `api:${response.status}:${path}`,
      context: { status: response.status, url: response.url, request_id: response.headers.get('x-request-id') || '' },
    })
  }

  if (errorData && decision !== 'suppress') {
    const { message, details, actions } = formatErrorMessage(errorData)
    const override = typeof decision === 'object' ? decision : undefined

    emitToast({
      message: override?.message ?? message,
      details,
      actions,
      request_id: response.headers.get('x-request-id') || '',
      type: override?.type,
    })
  }

  if (!shouldIgnorePath()) {
    if (response.status === 404) {
      await checkSession()
    }

    if (response.status === 401) {
      try {
        await fetch(CHECK_SESSION_URL, { method: 'DELETE', credentials: 'include' })
      } catch {
        // ignore
      }
      clearSessionCookies()
    }
  }

  return response._data
}

export const onRequestError = ({ request, error }: { request: RequestInfo | URL; error: Error }): void => {
  // AbortError = TanStack Query cancelling on unmount/navigation — expected, not a failure.
  if (error?.name === 'AbortError') return

  // Network/transport failures (DNS, offline, CORS) never reach onResponseError.
  const url = request instanceof Request ? request.url : String(request)
  const path = normalizeUrlPath(url)
  emitError({
    message: `API network error ${path}`,
    error,
    source: 'api',
    fingerprint: `api:network:${path}`,
    context: { url },
  })
}

export const api: $Fetch = ofetch.create({
  baseURL: BASE_URL,
  credentials: 'include',
  onRequest({ request, options }) {
    const url = request instanceof Request ? request.url : String(request)
    onRequest(options, url)
  },
  onResponse({ response }) {
    onResponse(response as ResponseWithData)
  },
  async onResponseError({ response }) {
    throw await onResponseError(response as ResponseWithData)
  },
  onRequestError,
})

// Orval mutator signature: accepts a config object and optional request overrides
/* includeHeaders: true
 Enable this when you need the full response object (status, headers, etc.),
 for example to read `Content-Disposition` during file downloads.
 This switches to `api.raw()` because the default `api()` returns only parsed data. */

export async function orvalFetch<T>(
  config: {
    url: string
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
    params?: Any
    data?: Any
    headers?: Record<string, string>
    signal?: AbortSignal
    responseType?: 'blob' | 'arraybuffer' | 'json' | string
  },
  requestOverrides?: {
    headers?: Record<string, string>
    params?: Any
    includeHeaders?: boolean
  },
): Promise<T> {
  const headers = { ...(config.headers || {}), ...(requestOverrides?.headers || {}) }
  const params = { ...(config.params || {}), ...(requestOverrides?.params || {}) }

  const apiArgs: Any = {
    method: config.method,
    query: params,
    body: config.data,
    signal: config.signal,
  }

  if (config.responseType) {
    apiArgs.responseType = config.responseType
  }

  if (Object.keys(headers).length > 0) {
    apiArgs.headers = headers
  }

  if (requestOverrides?.includeHeaders) {
    return api.raw<T>(config.url, apiArgs) as Promise<T>
  }

  return api<T>(config.url, apiArgs)
}
